import { useCallback } from 'react';
import { Trans } from 'react-i18next';
import { connect, type ConnectedProps } from 'react-redux';
import { InformationalScreen, LoadingModal, Text } from '@adc/parallax-component-library';
import { LocalDate } from '@js-joda/core';
import { BirthdayInput, Footer } from 'Components/utility';
import { FormContainer } from 'Components/utility/FormContainer';
import { MainContainer } from 'Components/utility/MainContainer';
import { TopHeaderBackArrow } from 'Components/utility/TopHeaderBackArrow';
import { ApiStatus } from 'Constants';
import { type FormikHelpers, useFormik } from 'formik';
import { type RootState } from 'Reducers/index';
import { verifyDateOfBirth } from 'Services/verify';
import { notifyApp } from 'Utilities/appCommunicator';
import i18n from 'Utilities/i18n';
import mediator from 'Utilities/mediator';
import * as z from 'zod';
import { toFormikValidationSchema } from 'zod-formik-adapter';

type MyFormValues = {
  dateOfBirth: string | undefined;
};

const initialValues: MyFormValues = {
  dateOfBirth: undefined,
};

const mapStateToProps = ({
  env: { deviceId, osType, osVersion, app, appVersion },
  auth: {
    include: { patient },
  },
}: RootState) => ({
  deviceId,
  osType,
  osVersion,
  app,
  appVersion,
  patient,
});

const connector = connect(mapStateToProps);

type Props = ConnectedProps<typeof connector>;

const VerifyAccount = ({ deviceId, osType, osVersion, app, appVersion, patient }: Props) => {
  const validationSchema = z.object({
    dateOfBirth: z.iso.date(
      i18n.t('VerifyAccount.content.verifyAccountForm.formField.dateOfBirth.errors.required')
    ),
  });

  const onSubmit = useCallback(
    async ({ dateOfBirth }: MyFormValues, { setFieldError }: FormikHelpers<MyFormValues>) => {
      const { data } = await verifyDateOfBirth(
        dateOfBirth ?? '',
        osType,
        osVersion,
        app,
        appVersion,
        deviceId
      );

      if (data?.code === ApiStatus.DATE_OF_BIRTH_INVALID) {
        notifyApp('adc-webview:verify-account', { status: data.code, data });
        setFieldError(
          'dateOfBirth',
          i18n.t('VerifyAccount.content.verifyAccountForm.formField.dateOfBirth.errors.invalid')
        );

        return;
      }

      notifyApp('adc-webview:verify-account', data);
      mediator.publish('router:redirect', '/password-setup');
    },
    [app, appVersion, deviceId, osType, osVersion]
  );

  const {
    handleSubmit,
    setFieldTouched,
    setFieldValue,
    isValid,
    errors,
    touched,
    values,
    isSubmitting,
  } = useFormik({
    initialValues,
    validationSchema: toFormikValidationSchema(validationSchema),
    onSubmit,
    validateOnBlur: true,
  });

  const handleDateChange = useCallback(
    (date: LocalDate, cancelled: boolean) => {
      setFieldTouched('dateOfBirth');

      if (cancelled) {
        return;
      }

      const dateString = date.toString();

      setFieldValue('dateOfBirth', dateString);
    },
    [setFieldTouched, setFieldValue]
  );

  return (
    <MainContainer>
      {isSubmitting && <LoadingModal aria-label="Loading" />}

      <InformationalScreen
        topNavigationProps={{
          animatedTitle: true,
          title: i18n.t('VerifyAccount.title'),
          buttonStart: {
            element: <TopHeaderBackArrow testID="Auth.action-go-back" />,
            label: i18n.t('VerifyAccount.title'),
            onPress: () => {
              notifyApp('adc-webview:navigate-back-app');
            },
          },
          testID: 'Auth.actionVerifyAccountTitle',
        }}
      >
        <InformationalScreen.Page>
          <InformationalScreen.Content>
            <Text marginTop={-8}>
              <Trans
                i18n={i18n}
                i18nKey="VerifyAccount.subtitle"
                values={{
                  email: patient.email || 'janedoe@gmail.com',
                }}
                components={{
                  Bold: <Text fontWeight="bold" />,
                }}
              />
            </Text>

            <FormContainer hasHiddenUserInput>
              <BirthdayInput
                error={Boolean(errors.dateOfBirth)}
                errorMessage={errors.dateOfBirth}
                isTouched={touched.dateOfBirth}
                initialDate={values.dateOfBirth ? LocalDate.parse(values.dateOfBirth) : undefined}
                onDateChange={handleDateChange}
                testID="dateOfBirth"
                endDate={LocalDate.now().minusYears(18)}
              />

              {errors.dateOfBirth ===
                i18n.t(
                  'VerifyAccount.content.verifyAccountForm.formField.dateOfBirth.errors.invalid'
                ) && (
                <Text marginTop="$4">
                  {i18n.t(
                    'VerifyAccount.content.verifyAccountForm.formField.dateOfBirth.errors.invalidHelpText'
                  )}
                </Text>
              )}
            </FormContainer>
          </InformationalScreen.Content>

          <InformationalScreen.Footer>
            <Footer
              buttonLabel={i18n.t(
                'VerifyAccount.content.verifyAccountForm.footerCreateAccount.submit.label'
              )}
              onButtonPress={handleSubmit}
              buttonTestID="VerifyAccount.submit-btn"
              disabled={!isValid}
            />
          </InformationalScreen.Footer>
        </InformationalScreen.Page>
      </InformationalScreen>
    </MainContainer>
  );
};

export default connector(VerifyAccount);
